import { useEffect, useMemo, useState } from "react";
import {
  fetchSalesCoupon,
  fetchSalesCoupons,
  type SalesCouponItem
} from "../../lib/sales-coupons";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type StatusFilter = "all" | "active" | "expired" | "disabled" | "used";

function statusTone(status: SalesCouponItem["status"]) {
  if (status === "Active") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
  }
  if (status === "Expired") {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
  }
  return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300";
}

function couponBenefit(coupon: Pick<SalesCouponItem, "type" | "discount" | "maxDiscount">) {
  if (coupon.type === "FIXED") {
    return `${formatCurrency(coupon.discount)} off`;
  }
  if (coupon.maxDiscount > 0) {
    return `${coupon.discount}% off up to ${formatCurrency(coupon.maxDiscount)}`;
  }
  return `${coupon.discount}% off`;
}

export function SalesCouponsPage() {
  const [coupons, setCoupons] = useState<SalesCouponItem[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<SalesCouponItem | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalCoupons: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalCoupons: 0,
    activeCoupons: 0,
    expiredCoupons: 0,
    usedCoupons: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const metrics = useMemo(
    () => [
      { label: "Total offers", value: summary.totalCoupons },
      { label: "Active now", value: summary.activeCoupons },
      { label: "Expired", value: summary.expiredCoupons },
      { label: "Used", value: summary.usedCoupons }
    ],
    [summary]
  );

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("status", statusFilter);
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchSalesCoupons(params);
        if (!ignore) {
          setCoupons(result.coupons);
          setPagination(result.pagination);
          setSummary(result.summary);
          setSelectedCoupon((current) => {
            if (result.coupons.length === 0) {
              return null;
            }
            if (current && result.coupons.some((coupon) => coupon.id === current.id)) {
              return current;
            }
            return result.coupons[0] || null;
          });
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load coupons.");
          setCoupons([]);
          setSelectedCoupon(null);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [page, query, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter]);

  useEffect(() => {
    let ignore = false;

    async function loadDetail() {
      if (!selectedCoupon?.id) {
        return;
      }

      setLoadingDetail(true);
      try {
        const detail = await fetchSalesCoupon(selectedCoupon.id);
        if (!ignore) {
          setSelectedCoupon(detail);
        }
      } catch {
        if (!ignore) {
          setSelectedCoupon(null);
        }
      } finally {
        if (!ignore) {
          setLoadingDetail(false);
        }
      }
    }

    void loadDetail();
    return () => {
      ignore = true;
    };
  }, [selectedCoupon?.id]);

  async function copyCouponCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode((current) => (current === code ? null : current)), 1600);
    } catch {
      setCopiedCode(null);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales promotions</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">Coupons and offers</h1>
        <p className="mt-4 max-w-3xl text-nv-slate">
          Check live coupon codes, explain discount terms clearly, and reference current promotions without opening admin-only controls.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <div className="admin-card rounded-[28px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,1fr)]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="admin-card rounded-[28px] p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <input
                className="field-control px-4 py-3 text-sm"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by coupon code, type, or description"
                value={query}
              />
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setStatusFilter(event.target.value as StatusFilter)} value={statusFilter}>
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="disabled">Disabled</option>
                <option value="used">Used</option>
              </select>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

          <div className="grid gap-4 md:grid-cols-2">
            {loading ? (
              <div className="admin-card rounded-[28px] p-5 text-sm text-nv-slate">Loading promotions...</div>
            ) : coupons.length > 0 ? (
              coupons.map((coupon) => (
                <button
                  className={`admin-card flex h-full flex-col rounded-[28px] p-5 text-left transition hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)] ${
                    selectedCoupon?.id === coupon.id ? "ring-1 ring-cyan-300/70 dark:ring-cyan-700/70" : ""
                  }`}
                  key={coupon.id}
                  onClick={() => setSelectedCoupon(coupon)}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(coupon.status)}`}>
                      {coupon.status}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-nv-slate">{coupon.type === "FIXED" ? "Fixed" : "Percentage"}</span>
                  </div>
                  <p className="mt-4 text-2xl font-semibold tracking-tight text-nv-ink">{coupon.code}</p>
                  <p className="mt-2 text-sm font-semibold text-cyan-700 dark:text-cyan-300">{couponBenefit(coupon)}</p>
                  <p className="mt-3 flex-1 text-sm leading-6 text-nv-slate">{coupon.description || "No coupon description added."}</p>
                  <div className="mt-4 grid gap-2 text-sm text-nv-slate">
                    <p>Used {coupon.usageCount} times</p>
                    <p>{coupon.expiryDate ? `Expires ${formatDateTime(coupon.expiryDate)}` : "No expiry date"}</p>
                  </div>
                </button>
              ))
            ) : (
              <div className="admin-card rounded-[28px] p-5 text-sm text-nv-slate">No coupons matched the current view.</div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-1 py-2 text-sm text-nv-slate dark:border-white/10">
            <p>Page {pagination.page} of {pagination.totalPages} · {pagination.totalCoupons} coupons</p>
            <div className="flex items-center gap-2">
              <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasPreviousPage || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
              <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasNextPage || loading} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
            </div>
          </div>
        </div>

        <aside className="min-h-0 overflow-y-auto pr-1">
          <div className="admin-card rounded-[32px] p-6">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Offer detail</p>
            {loadingDetail ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Loading coupon detail...
              </div>
            ) : selectedCoupon ? (
              <div className="mt-6 space-y-5">
                <div className="rounded-[28px] bg-[linear-gradient(135deg,rgba(8,145,178,0.14),rgba(14,165,233,0.05))] p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">Coupon code</p>
                      <h2 className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{selectedCoupon.code}</h2>
                    </div>
                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(selectedCoupon.status)}`}>
                      {selectedCoupon.status}
                    </span>
                  </div>
                  <p className="mt-4 text-lg font-semibold text-cyan-700 dark:text-cyan-300">{couponBenefit(selectedCoupon)}</p>
                  <p className="mt-3 text-sm leading-7 text-nv-slate">{selectedCoupon.description || "No coupon description added."}</p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Discount type</p>
                    <p className="mt-2 text-sm font-semibold text-nv-ink">{selectedCoupon.type === "FIXED" ? "Fixed amount" : "Percentage"}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Usage count</p>
                    <p className="mt-2 text-sm font-semibold text-nv-ink">{selectedCoupon.usageCount}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Discount</p>
                    <p className="mt-2 text-sm font-semibold text-nv-ink">
                      {selectedCoupon.type === "FIXED" ? formatCurrency(selectedCoupon.discount) : `${selectedCoupon.discount}%`}
                    </p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Max discount</p>
                    <p className="mt-2 text-sm font-semibold text-nv-ink">
                      {selectedCoupon.maxDiscount > 0 ? formatCurrency(selectedCoupon.maxDiscount) : "No cap"}
                    </p>
                  </div>
                </div>

                <div className="field-soft p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Validity</p>
                  <div className="mt-2 grid gap-2 text-sm text-nv-ink">
                    <p>{selectedCoupon.enabled ? "Enabled" : "Disabled"}</p>
                    <p>{selectedCoupon.expiryDate ? `Expires ${formatDateTime(selectedCoupon.expiryDate)}` : "No expiry date"}</p>
                    <p>{selectedCoupon.addedOn ? `Created ${formatDateTime(selectedCoupon.addedOn)}` : "Creation date unavailable"}</p>
                  </div>
                </div>

                <div className="rounded-[24px] border border-dashed border-black/10 p-4 text-sm text-nv-slate dark:border-white/10">
                  Apply this code at checkout or during sales order capture. Discounts still respect the underlying coupon rules and expiry checks.
                </div>

                <button
                  className="brand-button w-full rounded-full px-5 py-3 text-sm font-semibold text-white"
                  onClick={() => void copyCouponCode(selectedCoupon.code)}
                  type="button"
                >
                  {copiedCode === selectedCoupon.code ? "Coupon code copied" : "Copy coupon code"}
                </button>
              </div>
            ) : (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select a coupon to inspect the offer terms and copy the code for a customer.
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
